import { PropsWithChildren, ReactElement } from "react";

import { css } from "@compiled/react";
import { defineMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import {
    TypographyStyles,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import { TitleCard } from "./components/card";
import { MotionContainer } from "./components/motion-container";
import { RowGrid } from "./layouts/row-grid";
import { LinkColumn } from "./layouts/subnav-link-columns";
import { useCurrentNav, DivOnHoverExit } from "./nav-links-container";

type TeamData = {
    id: string;
    title: ReturnType<typeof defineMessage>;
    content: ReturnType<typeof defineMessage>;
    href: string;
    icon: {
        normal: ReactElement;
        hover?: ReactElement;
    };
};

const icons = {
    software: {
        normal: (
            <svg
                width="36"
                height="36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path d="M32 0H0v20.953h32V0z" fill="#BF63F3" />
                <path
                    d="M24.596 23.823v3.561H7.404v-3.561h5.727v-6.391h5.738v6.39h5.727z"
                    fill="#FCA700"
                />
                <path
                    d="M18.869 17.432H13.13v3.527h5.738v-3.527z"
                    fill="#AE2A19"
                />
                <path
                    d="M16 15.45a4.97 4.97 0 1 0 0-9.941 4.97 4.97 0 0 0 0 9.941z"
                    fill="#fff"
                />
            </svg>
        ),
        hover: (
            <div css={css({ position: "relative" })}>
                <div
                    css={css({ position: "absolute", insetBlockStart: "-8px" })}
                >
                    <svg
                        width="38"
                        height="36"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M31.874 8.27H0v20.872h31.874V8.27z"
                            fill="#BF63F3"
                        />
                        <path
                            d="M24.499 32v3.547H7.375V32h5.704v-6.365h5.716v6.365h5.704z"
                            fill="#FCA700"
                        />
                        <path
                            d="M18.794 25.634H13.08v3.513h5.715v-3.513z"
                            fill="#AE2A19"
                        />
                        <path
                            d="M15.937 23.66a4.951 4.951 0 1 0 0-9.902 4.951 4.951 0 0 0 0 9.902z"
                            fill="#fff"
                        />
                        <path
                            d="M32.588 8.506c.473-.04 6.793-1.54 5.135-1.865-.615.17-1.093-.002-1.4.456-.089-.54-1.295.322-1.48.355-.084-.41-1.973.644-1.697.264-.858.01-1.923.305-3.03.65-.544-.361 1.783-2.943.805-2.385.069-.145.332-.774.296-.358.31-.356 3.678-5.81 2.277-4.887-.945 1.125-1.654 3.01-2.454 3.942-.779.314-1.318 3.016-2.185 2.747C28.35 6.817 27.045-.62 26.12.04c.161.579.015 1.097.465 1.375-.55.087.328 1.27.361 1.453-.407.076.649 1.938.269 1.665.007.525.128 1.133.302 1.778-.232-.173-.485-.567-.632-.625-1.56-.62-2.742-2.046-4.366-2.818-1.366 1.707 10.707 6.014 3.221 6.657-1.408.902-3.279 1.04-4.85 1.908-.135 1.878 8.82-3.304 6.236-.328-.402 1.258-.998 2.671-1.823 3.911-.526 4.729 3.948-8.118 3.763-3.216.617 1.17 1.213 2.582 1.519 4.034 2.188 3.15-.799-5.181-1.112-6.361 1.186-.497 5.769 5.132 6.599 4.245.039-.125-.62-.822-.82-.896-.136.067-.204.121-.284-.002.402-.38-1.008-.86-1.141-.991.296-.29-1.666-1.23-1.19-1.215-.366-.503-.928-1.01-1.567-1.521 1.134-.215 1.627-1 1.523-.591l-.004.003z"
                            fill="#101214"
                        />
                    </svg>
                </div>
            </div>
        )
    },
    projectManagement: {
        normal: (
            <svg
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M35.9998 8.96556H8.53633V22.3844H35.9998V8.96556Z"
                    fill="#BF63F3"
                />
                <path
                    d="M27.4635 0.632355H0V14.0512H27.4635V0.632355Z"
                    fill="#FCA700"
                />
                <path
                    d="M27.4636 8.96556H8.53633V14.0512H27.4636V8.96556Z"
                    fill="#AE2A19"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M34.291 13.8143L28.3766 19.7288L25.3207 16.5883L26.64 15.3085L28.3991 17.1127L32.9942 12.5176L34.2967 13.82L34.291 13.8143Z"
                    fill="white"
                />
            </svg>
        ),
        hover: (
            <svg
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M35.9998 8.96556H8.53633V22.3844H35.9998V8.96556Z"
                    fill="#BF63F3"
                />
                <path
                    d="M27.4635 0.632355H0V14.0512H27.4635V0.632355Z"
                    fill="#FCA700"
                />
                <path
                    d="M27.4636 8.96556H8.53633V14.0512H27.4636V8.96556Z"
                    fill="#AE2A19"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M34.291 13.8143L28.3766 19.7288L25.3207 16.5883L26.64 15.3085L28.3991 17.1127L32.9942 12.5176L34.2967 13.82L34.291 13.8143Z"
                    fill="white"
                />
            </svg>
        )
    },
    marketing: {
        normal: (
            <svg
                width="36"
                height="36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M27.94.348 14.422 5.757v14.422l13.518 5.409V.348z"
                    fill="#1868DA"
                />
                <path
                    d="M17.886 6.655v12.601h-7.07v8.128H5.409v-8.128H0V6.655h17.886z"
                    fill="#FCA700"
                />
                <path
                    d="M17.885 6.655h-3.463V19.25h3.463V6.655z"
                    fill="#AE2A19"
                />
            </svg>
        ),
        hover: (
            <div css={css({ position: "relative" })}>
                <div
                    css={css({
                        position: "absolute",
                        insetBlockStart: "0.5px"
                    })}
                >
                    <svg
                        width="44"
                        height="28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M27.928 0 14.416 5.406v14.416l13.512 5.407V0z"
                            fill="#1868DA"
                        />
                        <path
                            d="M17.878 6.304V18.9h-7.066v8.124H5.406V18.9H0V6.304h17.878z"
                            fill="#FCA700"
                        />
                        <path
                            d="M17.878 6.304h-3.462v12.59h3.462V6.304z"
                            fill="#AE2A19"
                        />
                        <path
                            d="M40.19 24.62c4.537-6.676 3.624-12.212 2.897-15.873.641-1.943-4.478-10.684-3.118-5.24 2.002 2.338 2.987 10.27 1.072 14.64 1.044 1.28-4.4 9.485-.851 6.47v.003z"
                            fill="#101214"
                        />
                        <path
                            d="M36.972 21c2.443-2.596 2.184-6.41 1.992-7.982.792 1.265.427-2.103.024-2.649.034.533-.32.253-.359-.19.951-.14-1.278-5.99-2.428-4.397.382.71.04 1.062.799 1.126-.9.356.386 1.086.486 1.329-.176.52-.393.506.127 1.183 0 .906-.258 2.202.035 3.724.072 2.122-.607 3.275-.783 5.03-.282.74-2.087 4.434.1 2.832l.007-.007zM33.486 17.808c.906-1.04 1.44-2.352 1.32-4.19-.666.772-.648.05-.328-.444.73.98.551-2.102-.076-1.31-.151-.206-.058-.436.114-.279.54-1.15-2.105-4.664-1.533-1.806.468-.133-.675.374.444.75-.31.44-.479.3-.048.686-.027.407-.544 1.206-.213 2.022.282 1.163-.772 1.586-.156 2.459-.461.52-.947 2.998.48 2.105l-.004.007z"
                            fill="#101214"
                        />
                    </svg>
                </div>
            </div>
        )
    },
    design: {
        normal: (
            <svg
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M23.151 16.9483C23.2365 16.4729 23.2792 15.9602 23.2792 15.426C23.2792 14.8919 23.2365 14.3684 23.1457 13.8823C22.6275 11.0033 20.6298 9.22993 17.8202 8.77057C17.3395 8.69044 16.8374 8.65305 16.3193 8.65305C15.8012 8.65305 15.251 8.69579 14.7542 8.78125C11.95 9.2513 9.95229 11.0193 9.43416 13.8823C9.3487 14.3684 9.30063 14.8812 9.30063 15.426C9.30063 15.9708 9.34336 16.4729 9.42882 16.9483C9.9416 19.806 11.9393 21.5794 14.7542 22.0494C15.251 22.1349 15.7745 22.1776 16.3193 22.1776C16.8641 22.1776 17.3448 22.1402 17.8202 22.0601C20.6298 21.6007 22.6329 19.8274 23.1457 16.9483H23.151Z"
                    fill="#FCA700"
                />
                <path
                    d="M0 14.6674L8.33265 0.384415L16.6653 14.6674H0Z"
                    fill="#BF63F3"
                />
                <path
                    d="M13.4255 9.12296C11.321 9.86008 9.85742 11.4892 9.4301 13.8822C9.38203 14.1386 9.34998 14.395 9.32327 14.6674H16.6571L13.4255 9.12296Z"
                    fill="#AE2A19"
                />
                <path
                    d="M28.9048 16.676H17.1964V28.3844H28.9048V16.676Z"
                    fill="#82B536"
                />
                <path
                    d="M17.1964 16.6813V22.1402C17.41 22.1188 17.6183 22.0921 17.8266 22.0601C20.6362 21.6061 22.6339 19.8274 23.1521 16.9483C23.1681 16.8629 23.1841 16.7721 23.1948 16.6813H17.1964Z"
                    fill="#AE2A19"
                />
            </svg>
        ),
        hover: (
            <svg
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M23.151 16.9483C23.2365 16.4729 23.2792 15.9602 23.2792 15.426C23.2792 14.8919 23.2365 14.3684 23.1457 13.8823C22.6275 11.0033 20.6298 9.22993 17.8202 8.77057C17.3395 8.69044 16.8374 8.65305 16.3193 8.65305C15.8012 8.65305 15.251 8.69579 14.7542 8.78125C11.95 9.2513 9.95229 11.0193 9.43416 13.8823C9.3487 14.3684 9.30063 14.8812 9.30063 15.426C9.30063 15.9708 9.34336 16.4729 9.42882 16.9483C9.9416 19.806 11.9393 21.5794 14.7542 22.0494C15.251 22.1349 15.7745 22.1776 16.3193 22.1776C16.8641 22.1776 17.3448 22.1402 17.8202 22.0601C20.6298 21.6007 22.6329 19.8274 23.1457 16.9483H23.151Z"
                    fill="#FCA700"
                />
                <path
                    d="M0 14.6674L8.33265 0.384415L16.6653 14.6674H0Z"
                    fill="#BF63F3"
                />
                <path
                    d="M13.4255 9.12296C11.321 9.86008 9.85742 11.4892 9.4301 13.8822C9.38203 14.1386 9.34998 14.395 9.32327 14.6674H16.6571L13.4255 9.12296Z"
                    fill="#AE2A19"
                />
                <path
                    d="M28.9048 16.676H17.1964V28.3844H28.9048V16.676Z"
                    fill="#82B536"
                />
                <path
                    d="M17.1964 16.6813V22.1402C17.41 22.1188 17.6183 22.0921 17.8266 22.0601C20.6362 21.6061 22.6339 19.8274 23.1521 16.9483C23.1681 16.8629 23.1841 16.7721 23.1948 16.6813H17.1964Z"
                    fill="#AE2A19"
                />
            </svg>
        )
    },
    productManagement: {
        normal: (
            <svg
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.33503 0.384399H13.2446L14.7864 4.50468L19.1277 3.78376L21.5799 8.03559L18.7857 11.435L21.5799 14.8291L19.1277 19.0809L14.7917 18.36L13.2499 22.4802H8.34029L6.79847 18.36L2.46245 19.0809L0.0102768 14.8291L2.80449 11.435L0.0102768 8.04086L2.46245 3.78902L6.80374 4.50994L8.34555 0.389662L8.33503 0.384399ZM10.7872 15.571C13.0762 15.571 14.9285 13.7187 14.9285 11.4297C14.9285 9.14065 13.0762 7.28836 10.7872 7.28836C8.49816 7.28836 6.64587 9.14065 6.64587 11.4297C6.64587 13.7187 8.49816 15.571 10.7872 15.571Z"
                    fill="#82B536"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M22.169 10.1458H26.2209L27.4943 13.5451L31.0779 12.9505L33.1038 16.4604L30.7937 19.2651L33.0985 22.0698L31.0726 25.5797L27.4891 24.9851L26.2156 28.3844H22.1638L20.8903 24.9851L17.3068 25.5797L15.2808 22.0698L17.5857 19.2651L15.2808 16.4604L17.3068 12.9505L20.8903 13.5451L22.1638 10.1458H22.169ZM24.195 22.6855C26.0841 22.6855 27.6154 21.1542 27.6154 19.2651C27.6154 17.376 26.0841 15.8447 24.195 15.8447C22.3058 15.8447 20.7745 17.376 20.7745 19.2651C20.7745 21.1542 22.3058 22.6855 24.195 22.6855Z"
                    fill="#FCA700"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.7859 11.4297L21.5802 8.03559L19.128 3.78376L14.7867 4.50468L13.2449 0.389664H8.33528L6.7882 4.50468L2.45217 3.78376L0 8.03559L2.79421 11.4297L0 14.8291L2.45217 19.0809L6.7882 18.3547L8.33528 22.475H13.2449L14.7867 18.3547L17.1757 18.7546L19.128 19.0809L21.5802 14.8291L20.4646 13.4714L18.7859 11.4297ZM10.7874 15.571C8.50366 15.571 6.64612 13.7187 6.64612 11.4297C6.64612 9.14065 8.50366 7.28837 10.7874 7.28837C13.0712 7.28837 14.9288 9.14591 14.9288 11.4297C14.9288 13.7135 13.0765 15.571 10.7874 15.571Z"
                    fill="#82B536"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M20.465 13.4714L17.3129 12.9505L15.287 16.4603L17.1761 18.7546L19.1284 19.0809L21.5806 14.8291L20.465 13.4714Z"
                    fill="#AE2A19"
                />
            </svg>
        ),
        hover: (
            <svg
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M8.33503 0.384399H13.2446L14.7864 4.50468L19.1277 3.78376L21.5799 8.03559L18.7857 11.435L21.5799 14.8291L19.1277 19.0809L14.7917 18.36L13.2499 22.4802H8.34029L6.79847 18.36L2.46245 19.0809L0.0102768 14.8291L2.80449 11.435L0.0102768 8.04086L2.46245 3.78902L6.80374 4.50994L8.34555 0.389662L8.33503 0.384399ZM10.7872 15.571C13.0762 15.571 14.9285 13.7187 14.9285 11.4297C14.9285 9.14065 13.0762 7.28836 10.7872 7.28836C8.49816 7.28836 6.64587 9.14065 6.64587 11.4297C6.64587 13.7187 8.49816 15.571 10.7872 15.571Z"
                    fill="#82B536"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M22.169 10.1458H26.2209L27.4943 13.5451L31.0779 12.9505L33.1038 16.4604L30.7937 19.2651L33.0985 22.0698L31.0726 25.5797L27.4891 24.9851L26.2156 28.3844H22.1638L20.8903 24.9851L17.3068 25.5797L15.2808 22.0698L17.5857 19.2651L15.2808 16.4604L17.3068 12.9505L20.8903 13.5451L22.1638 10.1458H22.169ZM24.195 22.6855C26.0841 22.6855 27.6154 21.1542 27.6154 19.2651C27.6154 17.376 26.0841 15.8447 24.195 15.8447C22.3058 15.8447 20.7745 17.376 20.7745 19.2651C20.7745 21.1542 22.3058 22.6855 24.195 22.6855Z"
                    fill="#FCA700"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.7859 11.4297L21.5802 8.03559L19.128 3.78376L14.7867 4.50468L13.2449 0.389664H8.33528L6.7882 4.50468L2.45217 3.78376L0 8.03559L2.79421 11.4297L0 14.8291L2.45217 19.0809L6.7882 18.3547L8.33528 22.475H13.2449L14.7867 18.3547L17.1757 18.7546L19.128 19.0809L21.5802 14.8291L20.4646 13.4714L18.7859 11.4297ZM10.7874 15.571C8.50366 15.571 6.64612 13.7187 6.64612 11.4297C6.64612 9.14065 8.50366 7.28837 10.7874 7.28837C13.0712 7.28837 14.9288 9.14591 14.9288 11.4297C14.9288 13.7135 13.0765 15.571 10.7874 15.571Z"
                    fill="#82B536"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M20.465 13.4714L17.3129 12.9505L15.287 16.4603L17.1761 18.7546L19.1284 19.0809L21.5806 14.8291L20.465 13.4714Z"
                    fill="#AE2A19"
                />
            </svg>
        )
    },
    it: {
        normal: (
            <svg
                width="36"
                height="36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M0 4.648v18.736l5.426-4.652h16.289V4.648H0z"
                    fill="#FCA700"
                />
                <path
                    d="M9.644 5.204a2.405 2.405 0 0 1 2.399-2.42 2.402 2.402 0 0 1 2.398 2.42c0 .684-.163.996-.312 1.195-.177.231-.398.394-.787.68-.064.049-.136.099-.213.153-.484.357-1.1.846-1.562 1.656-.466.815-.724 1.838-.724 3.191h2.399c0-1.05.194-1.634.403-1.991.208-.367.493-.616.91-.924.05-.036.104-.077.162-.117.376-.272.91-.656 1.313-1.186.52-.679.81-1.53.81-2.657 0-2.652-2.127-4.82-4.797-4.82s-4.798 2.168-4.798 4.82v1.2h2.399v-1.2zM12.042 13.885a1.497 1.497 0 1 0 0 2.996 1.497 1.497 0 1 0 0-2.996z"
                    fill="#1868DB"
                />
                <path
                    d="M7.272 4.648h2.435a2.26 2.26 0 0 0-.068.556v1.2H7.24v-1.2c0-.185.01-.37.032-.556zM13.54 15.383a1.5 1.5 0 1 1-3.002.003 1.5 1.5 0 0 1 3.002-.003zM16.84 5.204c0 1.132-.29 1.978-.81 2.657-.403.53-.937.914-1.313 1.186-.059.045-.113.081-.163.117-.416.313-.701.562-.91.928-.207.362-.402.946-.402 1.992h-2.399c0-1.354.254-2.376.72-3.187.466-.81 1.082-1.298 1.561-1.66.077-.055.15-.105.213-.154.394-.29.61-.448.787-.68.154-.203.317-.515.317-1.199 0-.19-.022-.38-.068-.556h2.435c.023.185.032.37.032.556z"
                    fill="#AE2A19"
                />
            </svg>
        ),
        hover: (
            <div css={css({ position: "relative" })}>
                <div
                    css={css({
                        position: "absolute",
                        insetBlockStart: "-4.5px"
                    })}
                >
                    <svg
                        width="32"
                        height="28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M0 9.225v18.651l5.402-4.63h16.214V9.224H0z"
                            fill="#FCA700"
                        />
                        <path
                            d="M9.6 9.78a2.394 2.394 0 0 1 2.388-2.411 2.391 2.391 0 0 1 2.388 2.41c0 .68-.162.991-.311 1.19-.176.23-.396.391-.784.675-.063.05-.135.1-.212.153-.482.356-1.094.843-1.554 1.65-.464.81-.72 1.828-.72 3.175h2.387c0-1.045.194-1.626.401-1.982.207-.365.491-.613.905-.919.05-.036.104-.076.163-.117.374-.27.905-.653 1.306-1.18.518-.676.807-1.523.807-2.645 0-2.64-2.118-4.798-4.776-4.798S7.213 7.14 7.213 9.78v1.194H9.6V9.779zM11.988 18.42a1.49 1.49 0 1 0 0 2.982 1.49 1.49 0 1 0 0-2.982z"
                            fill="#1868DB"
                        />
                        <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M7.24 9.225h2.424a2.249 2.249 0 0 0-.068.554v1.194H7.208V9.779c0-.185.01-.37.032-.554zM13.48 19.911a1.494 1.494 0 1 1-2.989.004 1.494 1.494 0 0 1 2.988-.004zM16.764 9.78c0 1.125-.288 1.968-.807 2.644-.4.527-.932.91-1.306 1.18-.059.045-.113.081-.162.117-.415.311-.699.559-.906.924-.207.36-.4.941-.4 1.982h-2.389c0-1.347.253-2.365.717-3.172.464-.806 1.077-1.293 1.554-1.653.077-.054.149-.104.212-.153.392-.289.608-.446.784-.676.153-.203.315-.514.315-1.194 0-.19-.022-.378-.067-.554h2.423c.023.185.032.37.032.554z"
                            fill="#AE2A19"
                        />
                        <path
                            d="M20.314 7.52c-.09.01-.597-2.335-.505-3.32-.329-.164-.641-.63-.675-1.145-1.403-.876-.352 1.613-.008 2.385.17-.56.12.815.31 1.006-.022-.114.428 2.163.796 1.483.062.066.186-.485.083-.408h-.001zM25.199 4.573c-.134-.499.044-.95.36-1.394-.379-1.735-1.477 1.183-1.734 1.893 0 0-.478 1.239-.498 1.094-.218.113-1.336 1.878-.498 1.754.217-.28.38-.93.642-.95.57-.186 1.084-1.866 1.727-2.4v.003zM29.763 8.753c.178-1.131-1.833-.1-2.647.021 0 0-.85.079-1.069.251.124-.01-2.874.447-1.24.878a33.42 33.42 0 0 1 1.08-.175c1.393.2 2.793-.363 3.877-.977l-.001.002z"
                            fill="#101214"
                        />
                    </svg>
                </div>
            </div>
        )
    }
} as const;

function useTeamsData(): Array<TeamData> {
    return [
        {
            id: "teams.software",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.software.title",
                defaultMessage: "Software"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.software.content",
                defaultMessage: "Ship high-quality, innovative software faster"
            }),
            href: useLocalizedHref("/teams/software-development"),
            icon: icons.software
        },
        {
            id: "teams.marketing",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.marketing.title",
                defaultMessage: "Marketing"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.marketing.content",
                defaultMessage: "Collaborative strategies for marketing success"
            }),
            href: useLocalizedHref("/teams/marketing"),
            icon: icons.marketing
        },
        // {
        //     id: "teams.project-management",
        //     title: defineMessage({
        //         id: "globalnavv2.subnav.teams.projectmanagement.title",
        //         defaultMessage: "Project management"
        //     }),
        //     content: defineMessage({
        //         id: "globalnavv2.subnav.teams.projectmanagement.content",
        //         defaultMessage:
        //             "Plan, track, and optimize projects for smooth delivery"
        //     }),
        //     // TODO: FIGURE OUT LINK
        //     href: "#",
        //     icon: icons.projectManagement
        // },
        // {
        //     id: "teams.design",
        //     title: defineMessage({
        //         id: "globalnavv2.subnav.teams.design.title",
        //         defaultMessage: "Design"
        //     }),
        //     content: defineMessage({
        //         id: "globalnavv2.subnav.teams.design.content",
        //         defaultMessage:
        //             "Align on user needs and craft breathtaking experiences"
        //     }),
        //     // TODO: FIGURE OUT LINK
        //     href: "#",
        //     icon: icons.design
        // },
        // {
        //     id: "teams.product-management",
        //     title: defineMessage({
        //         id: "globalnavv2.subnav.teams.productmanagement.title",
        //         defaultMessage: "Product management"
        //     }),
        //     content: defineMessage({
        //         id: "globalnavv2.subnav.teams.productmanagement.content",
        //         defaultMessage:
        //             "Master the product lifecycle, from brainstorm to launch"
        //     }),
        //     // TODO: FIGURE OUT LINK
        //     href: "#",
        //     icon: icons.productManagement
        // },
        {
            id: "teams.it",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.it.title",
                defaultMessage: "IT"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.it.content",
                defaultMessage:
                    "Deliver exceptional operations and support services."
            }),
            href: useLocalizedHref("/teams/it"),
            icon: icons.it
        }
    ];
}

function useTeamsLinks() {
    return {
        solution: {
            id: "solution",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.links.solution",
                defaultMessage: "Solution"
            }),
            items: [
                {
                    id: "work-management",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.work-management",
                        defaultMessage: "Work Management"
                    }),
                    href: useLocalizedHref("/solutions/work-management")
                },
                {
                    id: "it-service-management",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.it-service-management",
                        defaultMessage: "IT Service Management"
                    }),
                    href: useLocalizedHref("/solutions/itsm")
                },
                {
                    id: "agile-devops",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.agile-devops",
                        defaultMessage: "Agile & DevOps"
                    }),
                    href: useLocalizedHref("/solutions/devops")
                }
            ]
        },
        byTeamSize: {
            id: "byTeamSize",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.links.by-team-size",
                defaultMessage: "By Team Size"
            }),
            items: [
                {
                    id: "enterprise",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.enterprise",
                        defaultMessage: "Enterprise"
                    }),
                    href: useLocalizedHref("/enterprise")
                },
                {
                    id: "small-business",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.small-business",
                        defaultMessage: "Small Business"
                    }),
                    href: useLocalizedHref("/software/small-business")
                },
                {
                    id: "startup",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.startup",
                        defaultMessage: "Startup"
                    }),
                    href: useLocalizedHref("/software/startups")
                },
                {
                    id: "non-profit",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.non-profit",
                        defaultMessage: "Non-profit"
                    }),
                    href: useLocalizedHref("/teams/nonprofits")
                }
            ]
        },
        byIndustry: {
            id: "byIndustry",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.links.by-industry",
                defaultMessage: "By Industry"
            }),
            items: [
                {
                    id: "retail",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.retail",
                        defaultMessage: "Retail"
                    }),
                    href: useLocalizedHref("/industries/retail")
                },
                {
                    id: "telecommunications",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.telecommunications",
                        defaultMessage: "Telecommunications"
                    }),
                    href: useLocalizedHref("/industries/telecom")
                },
                {
                    id: "professional-services",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.professional-services",
                        defaultMessage: "Professional services"
                    }),
                    href: useLocalizedHref("/industries/professional-services")
                },
                {
                    id: "government",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.government",
                        defaultMessage: "Government"
                    }),
                    href: useLocalizedHref("/government")
                }
            ]
        }
    } as const;
}

const TeamsGrid = ({ children }: PropsWithChildren) => {
    return (
        <RowGrid
            numCols={3}
            rowGap={token("space.500")}
            columnGap={token("space.400")}
            paddingBlock={token("space.500")}
            paddingInline={token("space.500")}
        >
            {children}
        </RowGrid>
    );
};

export const TeamsSubnav = ({
    productKey = "home"
}: {
    productKey?: ProductKeyTypes;
}) => {
    const intl = useIntl();

    const teamsData = useTeamsData();
    const teamsLinks = useTeamsLinks();
    const linkColumns = Object.values(teamsLinks);

    const { direction } = useCurrentNav();

    return (
        <DivOnHoverExit>
            <TypographyStyles>
                <MotionContainer direction={direction}>
                    <TeamsGrid>
                        {teamsData.map(({ id, title, content, ...rest }) => (
                            <TitleCard
                                key={id}
                                title={intl.formatMessage(title)}
                                content={intl.formatMessage(content)}
                                productKey={productKey}
                                {...rest}
                            />
                        ))}
                    </TeamsGrid>
                    <div css={css({ backgroundColor: "#F8F8F8" })}>
                        <RowGrid
                            numCols={3}
                            columnGap={token("space.400")}
                            paddingBlock={token("space.500")}
                            paddingInline={token("space.500")}
                        >
                            {linkColumns.map((column) => {
                                return (
                                    <LinkColumn key={column.id}>
                                        <span
                                            css={css({
                                                textTransform: "uppercase",
                                                color: "#101214",
                                                fontSize: "0.75rem",
                                                fontFamily:
                                                    "var(--bxp-charlie-text-font-family)",
                                                fontWeight: 700,
                                                lineHeight: "1rem"
                                            })}
                                        >
                                            {intl.formatMessage(column.title)}
                                        </span>
                                        <div
                                            css={css({
                                                display: "grid",
                                                gridTemplateRows:
                                                    "40px 40px 40px 40px"
                                            })}
                                        >
                                            {column.items.map((link) => {
                                                return (
                                                    <div
                                                        key={link.id}
                                                        css={css({
                                                            a: {
                                                                "fontWeight": 400,
                                                                "fontSize":
                                                                    "0.875rem",
                                                                "color":
                                                                    "#101214",
                                                                "&:visited": {
                                                                    textDecoration:
                                                                        "none",
                                                                    color: "#101214"
                                                                },
                                                                "&:hover, &:focus":
                                                                    {
                                                                        color: "#0747A6"
                                                                    }
                                                            },

                                                            display: "flex",
                                                            alignItems: "end"
                                                        })}
                                                    >
                                                        <Link
                                                            type="anchor"
                                                            href={link.href}
                                                            text={intl.formatMessage(
                                                                link.text
                                                            )}
                                                            ariaLabel={intl.formatMessage(
                                                                link.text
                                                            )}
                                                            dataEventProps={{
                                                                "data-event":
                                                                    "clicked",
                                                                "data-event-component":
                                                                    "linkButton",
                                                                "data-event-container":
                                                                    "globalNavSubnav",
                                                                "data-product-key":
                                                                    productKey
                                                            }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </LinkColumn>
                                );
                            })}
                        </RowGrid>
                    </div>
                </MotionContainer>
            </TypographyStyles>
        </DivOnHoverExit>
    );
};
